import { motion } from 'framer-motion';
import React__default, { forwardRef } from 'react';
import { AkEditorCloseIcon, AkCheckIcon } from '@fuegokit/fuegoicons-react';
import Box from '../Box/Box.js';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';
import Spinner from '../Spinner/Spinner.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const MotionBox = motion(Box);
const AnimatedCloseIcon = motion(AkEditorCloseIcon);
motion.span;
const animationConfig = {
  transition: {
    duration: 0.25
  },
  exit: {
    scale: 0.5,
    opacity: 0
  },
  initial: {
    scale: 0.5,
    opacity: 0
  },
  animate: {
    scale: 1,
    opacity: 1
  }
};
/** Icon to pass into the step */
const StepIcon = /*#__PURE__*/forwardRef(({
  ...props
}, forwardedRef) => {
  const innerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  const {
    isCompletedStep,
    isCurrentStep,
    isError,
    isLoading,
    isKeepError,
    icon: CustomIcon,
    index,
    checkIcon: CustomCheckIcon
  } = props;
  const labelStyles = {
    fontWeight: 'medium',
    textAlign: 'center',
    fontSize: 1,
    color: isCompletedStep ? 'text.inverse' : 'text.default'
  };
  const Icon = React__default.useMemo(() => CustomIcon || null, [CustomIcon]);
  const Check = React__default.useMemo(() => CustomCheckIcon || AkCheckIcon, [CustomCheckIcon]);
  return React__default.useMemo(() => {
    // if the step has completed
    if (isCompletedStep) {
      // if there's an error, but we're proceeding
      if (isError && isKeepError) {
        // danger
        return /*#__PURE__*/React__default.createElement(Box, {
          sx: {
            backgroundColor: 'background.danger.bold.default'
          }
        }, /*#__PURE__*/React__default.createElement(AnimatedCloseIcon, _extends({
          key: "icon"
        }, animationConfig)));
      }
      // else, if step completed, normal check + success color
      return /*#__PURE__*/React__default.createElement(MotionBox, _extends({
        key: "check-icon",
        sx: {
          color: 'text.inverse'
        }
      }, animationConfig), /*#__PURE__*/React__default.createElement(Check, {
        fill: "currentColor"
      }));
    }
    if (isCurrentStep) {
      if (isError) return /*#__PURE__*/React__default.createElement(AnimatedCloseIcon, _extends({
        key: "icon"
      }, animationConfig));
      if (isLoading) return /*#__PURE__*/React__default.createElement(Spinner, {
        size: "small"
      });
    }
    if (Icon) {
      return /*#__PURE__*/React__default.createElement(MotionBox, _extends({
        key: "step-icon",
        sx: {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          backgroundColor: isCompletedStep ? 'background.success.bold.default' : isError ? 'background.danger.bold.default' : '',
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          color: isCompletedStep ? 'text.success' : isError ? 'text.inverse' : 'text.default'
        }
      }, animationConfig), /*#__PURE__*/React__default.createElement(Icon, null));
    }
    return /*#__PURE__*/React__default.createElement(MotionBox, _extends({
      ref: innerRef,
      key: "label",
      sx: labelStyles
    }, animationConfig), (index || 0) + 1);
  }, [isCompletedStep, isCurrentStep, isError, isLoading, Icon]);
});
StepIcon.displayName = 'StepIcon';

export { StepIcon };
